import { FC, useState } from 'react'

import styled from 'styled-components'
import { useDisclosure } from '@mantine/hooks'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { notifications } from '@mantine/notifications'

import {
  AddEquipmentBody,
  Equipment,
  EquipmentQueryParams,
  useAddEquipmentMutation,
  useArchiveEquipmentMutation,
  useGetAllEquipmentQuery,
  useUpdateEquipmentMutation,
  UpdateEquipmentBody,
} from 'src/Redux/api/equipmentApi.ts'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import { SearchBlock } from 'src/Pages/Transport/SearchBlock.tsx'
import { TransportTable } from 'src/Pages/Transport/TransportTable.tsx'
import { EditTransportModal } from 'src/Pages/Transport/EditTransportModal.tsx'
import {
  useAddModelMutation,
  useGetAllModelsQuery,
} from 'src/Redux/api/modelApi.ts'
import { colors, device } from 'src/Helpers/constants.ts'
import { Role, useGetProfileQuery } from 'src/Redux/api/authApi.ts'
import { useGetAllModelTypesQuery } from 'src/Redux/api/modelTypesApi.ts'
import { TableHeader } from 'src/Pages/Transport/TableHeader.tsx'

import { AddEquipmentForm, AddTransportModal } from './AddTransportModal'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
    width: 100%;
  }
`

const Text = styled.span`
  font-size: 14px;
  max-width: 200px;
  width: 100%;
  text-align: left;
  color: ${colors.thirdBlue};
  font-weight: 400;
`

export interface SearchTransportFormData {
  searchTerm?: string
  selectedType?: string
  ownership?: string
}

export const TransportPage: FC = () => {
  const { data: modelTypes } = useGetAllModelTypesQuery({
    all: true,
  })
  const [equipmentToEdit, setEquipmentToEdit] = useState<Equipment | null>()
  const [isAddModalOpened, { open: onOpenAddModal, close: onCloseAddModal }] =
    useDisclosure(false)
  const [
    isEditModalOpened,
    { open: onOpenEditModal, close: onCloseEditModal },
  ] = useDisclosure(false)
  const { data: user } = useGetProfileQuery()
  const isArchiveTransportAvailable =
    user?.role === Role.ADMIN || user?.role === Role.LEAD_MANAGER
  const { control, watch } = useForm<SearchTransportFormData>()

  const { searchTerm, selectedType, ownership } = watch()

  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000)

  const queryParams: EquipmentQueryParams = {
    name: debouncedSearchTerm,
    typeId: selectedType,
    isOwned: ownership || undefined,
    limit: 999,
  }

  const { data: equipmentResponse, isLoading } =
    useGetAllEquipmentQuery(queryParams)

  const [addEquipment] = useAddEquipmentMutation()

  const [updateEquipment] = useUpdateEquipmentMutation()
  const [archiveEquipment] = useArchiveEquipmentMutation()

  const { data: allModels } = useGetAllModelsQuery({
    all: true,
  })

  const [addModel] = useAddModelMutation()

  const showAddSuccess = (): void => {
    onCloseAddModal()
    notifications.show({
      title: 'Транспортное средство успешно добавлено',
      message: '',
      position: 'top-right',
    })
  }

  const showAddError = (error: unknown): void => {
    const message =
      typeof error === 'object' &&
      error !== null &&
      'data' in error &&
      typeof (error as { data?: { error?: string } }).data?.error === 'string'
        ? (error as { data: { error: string } }).data.error
        : 'Не удалось добавить технику'
    notifications.show({
      title: 'Ошибка',
      message,
      color: 'red',
      position: 'top-right',
    })
  }

  const onAddEquipment = (equipment: AddEquipmentForm): void => {
    const toEquipmentBody = (modelId: string): AddEquipmentBody => ({
      modelId,
      year: Number(equipment.year),
      registrationNumber: equipment.registrationNumber,
      engineNumber: equipment.engineNumber,
      color: equipment.color,
      vin: equipment.vin,
      passportNumber: equipment.passportNumber,
      estValue: equipment.estValue || undefined,
      isOwned: equipment.isOwned ?? true,
    })

    if (!equipment.modelId) {
      addModel({
        name: equipment.name || '',
        modelTypeId: Number(equipment.type) || 0,
        driveType: equipment.driveType,
        baseRentalCost: equipment.baseRentalCost || 0,
        fuelEfficiencySummer: equipment.fuelEfficiencySummer || 0,
        fuelEfficiencyWinter: equipment.fuelEfficiencyWinter || 0,
        engineCapacity: equipment.engineCapacity || 0,
        description: equipment.description || '',
        operationalLogs: equipment.operationalLogs || undefined,
        image: equipment.image,
      })
        .unwrap()
        .then((model) =>
          addEquipment(toEquipmentBody(String(model.id))).unwrap(),
        )
        .then(showAddSuccess)
        .catch(showAddError)
    } else {
      addEquipment(toEquipmentBody(equipment.modelId))
        .unwrap()
        .then(showAddSuccess)
        .catch(showAddError)
    }
  }

  const onArchiveTransport = (id: number): void => {
    archiveEquipment(id)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Транспортное средство отправлено в архив',
          message: '',
          position: 'top-right',
        })
      })
      .catch(() => {
        notifications.show({
          title: 'Не удалось архивировать транспортное средство',
          message: '',
          color: 'red',
          position: 'top-right',
        })
      })
  }

  const onEditEquipment = (equipment: Equipment): void => {
    onOpenEditModal()
    setEquipmentToEdit(equipment)
  }

  const onUpdateEquipment = (data: UpdateEquipmentBody): void => {
    if (equipmentToEdit) {
      updateEquipment({
        id: equipmentToEdit.id,
        data: data,
      }).then(() => {
        onCloseEditModal()
        notifications.show({
          title: 'Транспортное средство успешно изменено',
          message: '',
          position: 'top-right',
        })
      })
    }
  }

  return (
    <>
      <AddTransportModal
        onClose={onCloseAddModal}
        onAddEquipment={onAddEquipment}
        isOpened={isAddModalOpened}
        allModels={allModels?.data}
        modelTypes={modelTypes?.data}
      />
      <EditTransportModal
        onClose={onCloseEditModal}
        onUpdateEquipment={onUpdateEquipment}
        isOpened={isEditModalOpened}
        equipmentToEdit={equipmentToEdit}
        modelTypes={modelTypes?.data}
      />

      <Container>
        <TitleWrapper>
          <PageTitle>Каталог техники</PageTitle>

          <SearchBlock
            onOpenAddModal={onOpenAddModal}
            control={control}
            modelTypes={modelTypes?.data}
          />
        </TitleWrapper>
        <TableHeader />
        <TransportTable
          onArchiveTransport={onArchiveTransport}
          equipments={equipmentResponse?.data}
          isLoading={isLoading}
          onEditEquipment={onEditEquipment}
          isArchiveTransportAvailable={isArchiveTransportAvailable}
        />
        <Text>{`Всего техники: ${equipmentResponse?.meta.itemCount}`} </Text>
      </Container>
    </>
  )
}
